import {
  Box,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import OpenInFullOutlinedIcon from "@mui/icons-material/OpenInFullOutlined";
import { useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";

import { AtomicLogo } from "../../assets/AtomicLogo";
import { ChatContext } from "../../contexts/chat/context";
import { LayoutContext } from "../../contexts/layout/context";
import { VersionedDataContext } from "../../contexts/versioned_data/context";
import { useGuardedNavigation } from "../../hooks/use_guarded_navigation";
import { ChatStatus } from "./chat_status";
import { InputContainer } from "./input_container";
import { MessageContainer } from "./message_container";

const routeTitles: Record<string, string> = {
  "/run-history": "run history",
  "/demand": "demand",
  "/demand_preview": "demand",
  "/forecast-tracking": "forecast tracking",
  "/demand-walk-single": "demand walk",
  "/sku-cutovers": "sku cutovers",
  "/supply": "supply",
  "/supply-walk": "supply walk",
  "/supply-walk-single": "supply walk",
  "/inventory-health": "inventory health",
  "/inventory-health-with-transfers": "inventory health",
  "/inventory-health-mrp": "inventory health",
  "/raw-inventory-health": "inventory health",
  "/inventory": "inventory",
  "/transfer-planning": "transfer planning",
  "/transfer-recommendations": "transfer planning",
  "/po-export": "purchase orders",
  "/purchase-order-export-peach-and-lily": "purchase orders",
  "/po-inbox": "PO inbox",
  "/purchasing-calendar": "purchasing calendar",
  "/avanti-card-runs": "avanti card runs",
};

const toTitleCase = (value: string) =>
  value
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const getViewLabel = (pathname: string) => {
  const normalized = pathname.replace(/_preview$/, "");
  if (routeTitles[normalized]) {
    return toTitleCase(routeTitles[normalized]);
  }

  const segment = normalized.split("/").filter(Boolean)[0];
  if (!segment) return "Current View";

  return toTitleCase(segment.replace(/-/g, " "));
};

const getRunLabel = (value: unknown) => {
  if (!value || typeof value !== "object") {
    return "No run selected";
  }

  const run = value as {
    name?: string | null;
    displayId?: string | number | null;
    runSettings?: {
      settingsNameAndVersion?: string | null;
    };
  };

  if (run.name) return run.name;
  if (run.displayId) return `Run ${run.displayId}`;
  if (run.runSettings?.settingsNameAndVersion) {
    return run.runSettings.settingsNameAndVersion;
  }

  return "Current run";
};

export const Chat = () => {
  const { isConnected, disconnectChat } = useContext(ChatContext);
  const { setIsSidebarOpen } = useContext(LayoutContext);
  const { mainRun, lastSuccessfulRun } = useContext(VersionedDataContext);
  const { navigateWithGuards } = useGuardedNavigation();
  const location = useLocation();

  const currentRun = mainRun || lastSuccessfulRun;
  const currentViewLabel = useMemo(
    () => getViewLabel(location.pathname),
    [location.pathname],
  );
  const currentRunLabel = useMemo(() => getRunLabel(currentRun), [currentRun]);

  const handleClose = () => {
    setIsSidebarOpen(false);
    disconnectChat();
  };

  const handleExpand = () => {
    navigateWithGuards("/chat");
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "#fff",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.75,
          borderBottom: "1px solid",
          borderColor: "divider",
          background:
            "linear-gradient(180deg, rgba(25, 118, 210, 0.06) 0%, rgba(25, 118, 210, 0.01) 100%)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "12px",
                backgroundColor: "#fff",
                border: "1px solid",
                borderColor: "rgba(25, 118, 210, 0.18)",
                boxShadow: "0 2px 10px rgba(25, 118, 210, 0.08)",
              }}
            >
              <AtomicLogo width={24} height={24} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: "1rem",
                  fontWeight: 700,
                  color: "text.primary",
                  lineHeight: 1.2,
                }}
              >
                Nucleus
              </Typography>
              <Typography
                sx={{
                  mt: 0.25,
                  fontSize: "0.85rem",
                  color: "text.secondary",
                  lineHeight: 1.35,
                }}
              >
                Following {currentViewLabel.toLowerCase()}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip title="Open Nucleus full screen">
              <IconButton size="small" onClick={handleExpand}>
                <OpenInFullOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close Nucleus">
              <IconButton size="small" onClick={handleClose}>
                <CloseOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Typography
          sx={{
            mt: 1.1,
            fontSize: "0.85rem",
            color: "text.secondary",
            lineHeight: 1.45,
          }}
        >
          Ask about the run and the screen you are currently looking at.
        </Typography>

        <Box
          sx={{
            mt: 1.25,
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.76rem",
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            Current run
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            label={currentRunLabel}
            sx={{
              maxWidth: "100%",
              "& .MuiChip-label": {
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            }}
          />
        </Box>
      </Box>

      <Box sx={{ px: 2, py: 1.25 }}>
        <ChatStatus isConnected={isConnected} />
      </Box>

      <Divider />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 1,
            px: 1.5,
            py: 1,
          }}
        >
          <MessageContainer />
        </Box>

        <Box
          sx={{
            px: 1.5,
            pt: 1,
            pb: 1.5,
            display: "flex",
            alignItems: "flex-end",
            width: "100%",
            gap: 1,
            borderTop: "1px solid",
            borderColor: "divider",
            backgroundColor: "rgba(248, 250, 252, 0.65)",
          }}
        >
          <InputContainer />
        </Box>
      </Box>
    </Box>
  );
};
